import { FlagAvatar } from '@brig/theme/avatars';
import { Cartouche } from '@brig/theme/components';
import { RhumbLines } from '@brig/theme/ornaments';
import { button, paper } from '@brig/theme/styles';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { ApiRequestError } from '../api/client.ts';
import { useAcceptInvite, useInvite, useMe } from '../api/hooks.ts';
import { useAuth } from '../auth/useAuth.ts';
import { LoadingNotice } from '../components/LoadingNotice.tsx';
import { Sides } from '../game/AwaitingOpponent.tsx';
import { useDocumentTitle } from '../useDocumentTitle.ts';
import * as styles from './Invite.css.ts';

/**
 * `/join/:code`: an invite to an online game. Open to guests: they see who invites them and
 * sign in, or join the fleet first, and come back here to take command.
 */
export function Join() {
  const { code = '' } = useParams();
  const { t } = useTranslation();
  useDocumentTitle(t('invite.join.pageTitle'));
  const { status, signIn, signUp } = useAuth();
  const signedIn = status === 'authenticated';
  const invite = useInvite(code);
  const me = useMe(signedIn);
  const accept = useAcceptInvite();

  if (status === 'loading' || status === 'signing-out' || invite.isPending) return <LoadingNotice />;
  if (accept.data) return <Navigate to={`/games/${accept.data.id}`} replace />;

  const failure = accept.error instanceof ApiRequestError ? accept.error.code : null;
  const shown = invite.data?.status;
  const state = !invite.data
    ? 'missing'
    : failure === 'invite_taken' || (shown === 'accepted' && !signedIn)
      ? 'taken'
      : failure === 'invite_expired' || shown === 'expired' || shown === 'revoked' || shown === 'declined'
        ? 'expired'
        : 'open';
  const own = signedIn && me.data !== undefined && me.data.id === invite.data?.inviter.id;
  const returnTo = `/join/${code}`;

  return (
    <main className={styles.sea}>
      <RhumbLines originX={0.5} originY={0.5} opacity={0.12} />
      <section className={`${paper} ${styles.sheet}`}>
        <Cartouche kicker={t('invite.join.kicker')}>{t('invite.join.title')}</Cartouche>
        {invite.data ? (
          <>
            <p className={styles.sides}>
              <span className={styles.side}>
                <FlagAvatar id={invite.data.inviter.avatar} size={32} />
                <strong>{invite.data.inviter.nickname}</strong>
              </span>
            </p>
            <p className={styles.body}>
              {t(own ? 'invite.join.own' : 'invite.join.body', { name: invite.data.inviter.nickname })}
            </p>
            <Sides nations={invite.data.nations} />
          </>
        ) : null}
        {state === 'open' ? null : (
          <p className={styles.body} role="alert">
            {t(`invite.join.${state}`)}
          </p>
        )}
        {accept.isError && failure !== 'invite_taken' && failure !== 'invite_expired' ? (
          <p className={styles.body} role="alert">
            {t('invite.join.failed')}
          </p>
        ) : null}
        <div className={styles.actions}>
          {!signedIn && state === 'open' ? (
            <>
              <button
                type="button"
                className={button({ tone: 'seal' })}
                onClick={() => void signIn(returnTo)}
              >
                {t('invite.join.signIn')}
              </button>
              <button
                type="button"
                className={button({ tone: 'label' })}
                onClick={() => void signUp(returnTo)}
              >
                {t('invite.join.signUp')}
              </button>
            </>
          ) : null}
          {/* Taking an invite one already holds (or sent) just opens the game. */}
          {signedIn && invite.data && (state === 'open' || shown === 'accepted') && failure === null ? (
            <button
              type="button"
              className={button({ tone: 'seal' })}
              aria-disabled={accept.isPending}
              onClick={() => {
                if (!accept.isPending) accept.mutate(code);
              }}
            >
              {t(own || shown === 'accepted' ? 'invite.join.open' : 'invite.join.accept')}
            </button>
          ) : null}
          <Link to="/how-to-play" className={button({ tone: 'label' })}>
            {t('learn.open')}
          </Link>
          <Link to={signedIn ? '/harbour' : '/'} className={button({ tone: 'label' })}>
            {t(signedIn ? 'game.load.back' : 'invite.join.home')}
          </Link>
        </div>
      </section>
    </main>
  );
}
